'use client'

import { useEffect, useState } from 'react'
import { claimChunkReload, isChunkLoadError } from '@/lib/chunk-error'

/**
 * Automatic recovery for a chunk-load failure caught by a route error
 * boundary (#308). Returns true while a reload is in flight so the boundary
 * can render a calm "reconnecting" state instead of an error page the visitor
 * is about to navigate away from anyway.
 *
 * Non-chunk errors are left alone: reloading those just replays the failure.
 * The bound on how often this fires lives in `claimChunkReload`; once spent it
 * returns false forever for that path in that tab, and the boundary falls
 * through to its terminal state with a manual retry.
 */
export function useChunkErrorRecovery(error: unknown): boolean {
  const [reloading, setReloading] = useState(false)

  useEffect(() => {
    if (!isChunkLoadError(error)) return
    if (!claimChunkReload(window.location.pathname)) return
    setReloading(true)
    // A full document reload, not the boundary's `reset()`: reset() re-renders
    // the same tree inside the same document, where webpack's registry is
    // still missing the chunk and the asset manifest is still the one that
    // named it. Only reloading re-fetches the HTML, so a client holding a
    // stale manifest picks up the current one.
    window.location.reload()
  }, [error])

  return reloading
}
